import styles from './TabBar.module.css';

import type { FC, ReactNode } from 'react';

import classNames from 'classnames';
import styled from 'styled-components';
import { Link, useLocation } from 'react-router';
import { Car, User, House, Coins } from '@phosphor-icons/react';

import { isCurrentTab } from './tabCurrent';
import { hidesTabBar, useNavPlacement } from './useNavPlacement';

interface TabBarProps {
  /** The account session is held: Deals and Profile instead of Login (api#402, one session). */
  isLoggedIn?: boolean;
  /**
   * Where Profile leads when it is not the storefront's own screen: a dealer's profile in the dealer
   * cabinet (app#370, `dealerProfileUrl`). Another site's address, so the tab is a plain link to it —
   * the cabinet opens in this tab, and "open in a new tab" opens the cabinet, not `/profile`.
   */
  profileTo?: string;
}

interface Tab {
  to: string;
  icon: ReactNode;
  label: string;
}

export const TabBar: FC<TabBarProps> = (props) => {
  const { isLoggedIn, profileTo = '/profile' } = props;
  const location = useLocation();
  const placement = useNavPlacement();

  if (hidesTabBar(location.pathname)) {
    return null;
  }

  const tabs: Tab[] = [
    // The two catalogs (storefront-catalog spec §4.1). `/` still shows the sale and stays current
    // for "Listings"; a rental's page stays current for "Rentals" — see `isCurrentTab`.
    { to: '/buy', icon: <House size={20} weight="fill" />, label: 'Listings' },
    // Rental browse is public (discovery first, like Listings). The renter's own rentals live
    // under Deals → Rental Deals.
    { to: '/rent', icon: <Car size={20} weight="fill" />, label: 'Rentals' },
    ...(isLoggedIn
      ? [
          { to: '/deals', icon: <Coins size={20} weight="fill" />, label: 'Deals' },
          { to: profileTo, icon: <User size={20} weight="fill" />, label: 'Profile' },
        ]
      : [{ to: '/auth', icon: <User size={20} weight="fill" />, label: 'Login' }]),
  ];

  // A laptop's navigation runs across the top; the phone keeps its bar at the bottom as it was.
  if (placement === 'top') {
    return (
      <TopBar>
        {tabs.map((tab) => (
          <TopLink
            key={tab.to}
            to={tab.to}
            aria-current={isCurrentTab(tab.to, location.pathname) ? 'page' : undefined}
          >
            {tab.icon}
            {tab.label}
          </TopLink>
        ))}
      </TopBar>
    );
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.container}>
        {tabs.map((tab) => {
          const current = isCurrentTab(tab.to, location.pathname);

          return (
            <Link
              key={tab.to}
              to={tab.to}
              aria-current={current ? 'page' : undefined}
              className={classNames(styles.tabItem, current && 'active')}
            >
              {tab.icon}
              {tab.label}
            </Link>
          );
        })}
      </div>
    </div>
  );
};

/* Its height is `--gac-nav-height` (normalize.css), the same value the scroll container is moved
   down by, so nothing on a screen starts under the bar. */
const TopBar = styled.div`
  position: fixed;
  top: 0;
  bottom: auto;
  left: 0;
  right: 0;
  z-index: 1;
  height: var(--gac-nav-height);
  padding: 0 ${({ theme }) => theme.space.md}px;
  display: flex;
  flex-direction: row;
  align-items: stretch;
  gap: ${({ theme }) => theme.space.xs}px;
  background: ${({ theme }) => theme.colors.black};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

/* The active tab is the one place the accent goes here: a 2px underline, the bar's only lime. */
const TopLink = styled(Link)`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ theme }) => theme.space.xs}px;
  min-height: ${({ theme }) => theme.control.touchTarget}px;
  padding: 0 ${({ theme }) => theme.space.sm}px;
  font-family: ${({ theme }) => theme.fonts.text};
  font-size: ${({ theme }) => theme.typography.large.fontSize}px;
  line-height: ${({ theme }) => theme.typography.large.lineHeight}px;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  color: ${({ theme }) => theme.colors.lightWhite};
  text-decoration: none;
  border-bottom: 2px solid transparent;

  &[aria-current='page'] {
    color: ${({ theme }) => theme.colors.white};
    border-bottom-color: ${({ theme }) => theme.colors.accent};
  }

  &:focus-visible {
    outline: var(--gac-control-focus-ring) solid var(--gac-text-primary);
    outline-offset: 2px;
  }
`;
